import { useState } from 'react';

import { ConflictAlertDetailsModal } from './ConflictAlertDetailsModal';

import type { AISuggestionWarning } from '../../../lib/websocket/events';
import type { BlockedByConflictState } from '../hooks/use-ai-suggestion';
import type { ActiveConflictAlert } from '../hooks/use-conflict-alerts';

/**
 * ConflictWarningBanner — PR-B Fatia 7 (ADR-050 + Caderno cap 32) +
 * PR-B.2 fallback (Item 89) + r7b §32 modal de detalhes (ADR-117).
 *
 * Renderiza banner discreto sobre o Composer quando:
 *  - `blocked` populado → tenant Regulated; sugestão IA suprimida.
 *  - `warning` populado → Solo/Team/Pro; sugestão gerada normalmente mas com aviso.
 *  - `fallbackAlert` populado → modo redação manual sem sugestão IA pendente
 *    mas com `ConflictAlert.status='open'` envolvendo o bond da conversa
 *    (PR-B.2 via `useConflictAlerts`). Caller já garante que blocked+warning
 *    estão ausentes — não há duplicação.
 *
 * Prioridade: `blocked` > `warning` > `fallbackAlert`.
 *
 * r7b §32 refinamento: banner `fallbackAlert` ganha botão "Ver detalhes" que
 * abre `ConflictAlertDetailsModal` (ADR-117 §32.4).
 *
 * Fase 3.A DS Foundation cascade (Item 165):
 *   Adopta .conflict-alert-banner + .conflict-alert-banner--{severity} do DS
 *   @2.2.0 (components.css §§ 1889-1922 — entregue em Fase 1 Item 156).
 *   Tailwind Tailwind removido onde DS canonical cobre (text-red-800 bg-red-50
 *   border-red-200 / text-amber-800 bg-amber-50 border-amber-200 — todos
 *   substituídos por variante DS semântica).
 *   animation conflict-pulse box-shadow canônica auto-aplicada em --critical
 *   pelo DS (substituiu opacity-based divergente de global.css — Item 168).
 *   Sub-elements: .conflict-alert-banner__icon / __body / __meta / __actions.
 */
export function ConflictWarningBanner({
  blocked,
  warning,
  fallbackAlert,
}: {
  blocked: BlockedByConflictState | null;
  warning: AISuggestionWarning | undefined;
  fallbackAlert?: ActiveConflictAlert | null;
}): JSX.Element | null {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (blocked !== null) {
    /* blocked → critical: sugestão IA suprimida em tenant Regulated */
    return (
      <div
        role="alert"
        data-testid="conflict-blocked-banner"
        className="conflict-alert-banner conflict-alert-banner--critical"
      >
        <span aria-hidden className="conflict-alert-banner__icon">
          ⛔
        </span>
        <div className="conflict-alert-banner__body">
          <strong>Sugestão IA suspensa</strong>
          {' · '}
          <span>Conflito de interesse aberto</span>{' '}
          <span className="opacity-70">({blocked.alertCategory})</span>
        </div>
        <div className="conflict-alert-banner__meta ml-auto">{blocked.alertId.slice(0, 12)}</div>
      </div>
    );
  }
  if (warning !== undefined) {
    /* warning → high: sugestão gerada mas com aviso (Solo/Team/Pro) */
    return (
      <div
        role="status"
        data-testid="conflict-warning-banner"
        className="conflict-alert-banner conflict-alert-banner--high"
      >
        <span aria-hidden className="conflict-alert-banner__icon">
          ⚠
        </span>
        <div className="conflict-alert-banner__body">
          <strong>Atenção — conflito ativo</strong>
          {' · '}
          <span>Sugestão IA segue gerada; revise antes de enviar</span>
        </div>
        <div className="conflict-alert-banner__meta ml-auto">{warning.alertId.slice(0, 12)}</div>
      </div>
    );
  }
  if (fallbackAlert !== undefined && fallbackAlert !== null) {
    /* fallbackAlert → severity from ConflictAlert (low/medium/high/critical) */
    const severityClass = `conflict-alert-banner--${fallbackAlert.severity}`;
    return (
      <>
        <div
          role="status"
          data-testid="conflict-fallback-banner"
          className={`conflict-alert-banner ${severityClass}`}
        >
          <span aria-hidden className="conflict-alert-banner__icon">
            ⚠
          </span>
          <div className="conflict-alert-banner__body">
            <strong>Conflito de interesse ativo</strong>
            {' · '}
            <span className="opacity-80">{fallbackAlert.category}</span>
          </div>
          <div className="conflict-alert-banner__actions">
            <button
              type="button"
              onClick={() => setDetailsOpen(true)}
              className="text-2xs underline transition-colors"
              aria-label="Ver detalhes do ConflictAlert"
            >
              Ver detalhes
            </button>
          </div>
        </div>
        {/* r7b §32 — Modal de detalhes */}
        <ConflictAlertDetailsModal
          open={detailsOpen}
          alert={fallbackAlert}
          onClose={() => setDetailsOpen(false)}
        />
      </>
    );
  }
  return null;
}
